import JSZip from 'jszip';

import { SignaturesService } from '@client/services/signatures';

/**
 * Zip entry name for a user. Emails are unique, but sanitising can collide, so
 * `buildSignatureArchive` still dedupes.
 *
 * @param {string} email
 * @returns {string}
 */
export const entryName = (email) => `${String(email).replace(/[^A-Za-z0-9._@+-]/g, '_')}.html`;

/**
 * Renders one user's signature via the same endpoint the edit page's "Copy HTML"
 * uses. The empty `params`/`testData` mirror how `MySignature` mounts on the edit
 * page, where the server renders from the user's own stored data.
 *
 * @param {string} email
 * @returns {Promise<string|null>} the HTML, or null when there is nothing to save.
 */
export const renderSignature = async (email) => {
  const response = await SignaturesService.getSignature({
    username: email,
    params: {},
    testData: {},
    prependLines: 0
  });

  return response?.rendered || null;
};

/**
 * Bundles rendered signatures into a zip.
 *
 * @param {{ email: string, html: string }[]} entries
 * @returns {Promise<Blob|null>} null for an empty list, so the caller can skip the
 *   download rather than hand the user an empty archive.
 */
export const buildSignatureArchive = async (entries) => {
  if (!entries.length) {
    return null;
  }

  const zip = new JSZip();
  const used = new Set();

  entries.forEach(({ email, html }) => {
    let name = entryName(email);

    // Two emails can sanitise to the same name, and JSZip would silently overwrite.
    for (let n = 2; used.has(name); n += 1) {
      name = entryName(`${email}-${n}`);
    }

    used.add(name);
    zip.file(name, html);
  });

  return zip.generateAsync({ type: 'blob' });
};

/**
 * Matches the QR-code export's filename shape.
 *
 * @param {string} domain
 * @param {number} count
 * @returns {string}
 */
export const archiveFilename = (domain, count) => `${domain}-signatures(${count})-${new Date().getTime()}.zip`;
